import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import TutorialButton from './common/TutorialButton';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/desenho.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const DesenhoManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_escola: '',
    id_tipo_representacao: '',
    file: null,
    principal: true
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [escolaSearch, setEscolaSearch] = useState('');
  const [escolas, setEscolas] = useState([]);
  const [tiposRepresentacao, setTiposRepresentacao] = useState([]);
  const [showNamingConventionModal, setShowNamingConventionModal] = useState(false);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);
  const fileInputRef = useRef(null);

  // Filtrar escolas com base na busca
  const escolasFiltradas = useMemo(() => {
    if (!escolaSearch) return escolas;
    return escolas.filter(escola =>
      escola.nome_escola?.toLowerCase().includes(escolaSearch.toLowerCase()) ||
      escola.id_escola?.toString().includes(escolaSearch) ||
      escola.codigo_inep?.toString().includes(escolaSearch)
    );
  }, [escolas, escolaSearch]);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const fetchEscolas = useCallback(async () => {
    try {
      const response = await api.get('/escola');
      setEscolas(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar escolas: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchTiposRepresentacao = useCallback(async () => {
    try {
      const response = await api.get('/tipo-representacao');
      setTiposRepresentacao(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar tipos de representação: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/desenho');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão ou permissão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchEscolas();
    fetchTiposRepresentacao();
  }, [verificarAPI, fetchEscolas, fetchTiposRepresentacao]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/desenho');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'desenho', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/desenho/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'desenho', response.data);
      if (response && response.data.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
        carregarDados(false);
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'desenho');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem, carregarDados]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'desenho', { id });
    try {
      const response = await api.delete(`/desenho/${id}`);
      logarSucesso('Excluir', 'desenho', response.data);
      setDados(currentData => currentData.filter(item => item.id_desenho !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'desenho');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const getColunas = useMemo(() => {
    const escolasNomes = escolas.reduce((acc, escola) => {
      acc[escola.id_escola] = escola.nome_escola;
      return acc;
    }, {});

    const colunasBase = [
      {
        title: 'ID Desenho',
        field: 'id_desenho',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string',
      },
      {
        title: 'Escola',
        field: 'id_escola',
        width: 250,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Escola...',
        sorter: 'string',
        formatter: (cell) => escolasNomes[cell.getValue()] || cell.getValue(),
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeEscola = escolasNomes[rowValue] || rowValue || '';
          return nomeEscola.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: { values: escolasNomes }
      },
      {
        title: 'Tipo Projeto',
        field: 'id_tp_projeto',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Tipo...',
        sorter: 'string'
      },
      {
        title: 'Tipo Representação',
        field: 'id_tipo_representacao',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Representação...',
        sorter: 'string',
        formatter: (cell) => {
          const value = cell.getValue();
          const tipo = tiposRepresentacao.find(tr => tr.id_tipo_representacao === value);
          return tipo ? tipo.descricao : value;
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const tipo = tiposRepresentacao.find(tr => tr.id_tipo_representacao === rowValue);
          const nomeTipo = tipo ? tipo.descricao : (rowValue || '');
          return nomeTipo.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: () => {
          return {
            values: tiposRepresentacao.reduce((acc, tr) => {
              acc[tr.id_tipo_representacao] = tr.descricao;
              return acc;
            }, {})
          };
        }
      },
      {
        title: 'Arquivo Chave',
        field: 'arquivo_chave',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar arquivo...',
        sorter: 'string',
      },
      {
        title: 'Link Desenho Proj',
        field: 'link_desenho_proj',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar link...',
        sorter: 'string',
      },
      {
        title: 'Data Upload',
        field: 'data_upload',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar data...',
        sorter: 'date',
        editor: (cell, onRendered, success, cancel) => {
          const input = document.createElement("input");
          input.setAttribute("type", "text");
          input.setAttribute("placeholder", "DD/MM/YYYY");
          input.style.padding = "4px";
          input.style.width = "100%";
          input.style.boxSizing = "border-box";

          const value = cell.getValue();
          if (value) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const day = String(date.getUTCDate()).padStart(2, '0');
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const year = date.getUTCFullYear();
                input.value = `${day}/${month}/${year}`;
              } else {
                input.value = value;
              }
            } catch (e) {
              input.value = value;
            }
          }

          onRendered(() => {
            input.focus();
            input.style.height = "100%";
          });

          const handleDateInput = (e) => {
            const input = e.target;
            let value = input.value.replace(/\D/g, '');
            let formattedValue = '';
            if (value.length > 0) {
              formattedValue = value.substring(0, 2);
            }
            if (value.length > 2) {
              formattedValue += '/' + value.substring(2, 4);
            }
            if (value.length > 4) {
              formattedValue += '/' + value.substring(4, 8);
            }
            input.value = formattedValue;
          };

          input.addEventListener('input', handleDateInput);

          const onChange = () => {
            if (input.value && input.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
              const [day, month, year] = input.value.split('/');
              success(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
            } else {
              success(input.value);
            }
          };

          input.addEventListener("change", onChange);
          input.addEventListener("blur", onChange);

          return input;
        },
        formatter: (cell) => {
          const value = cell.getValue();
          if (!value) return '';
          try {
            const date = new Date(value);
            if (isNaN(date.getTime())) return value;
            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            return `${day}/${month}/${year}`;
          } catch (e) {
            return value;
          }
        }
      },
      {
        title: 'Nome Externo',
        field: 'nome_externo',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar nome...',
        sorter: 'string',
      },
      {
        title: 'Usuário',
        field: 'usuario',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar usuário...',
        sorter: 'string',
      },
      {
        title: 'Descrição',
        field: 'descricao',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar descrição...',
        sorter: 'string',
      },
      {
        title: 'Principal',
        field: 'principal',
        formatter: 'tickCross',
        hozAlign: 'center',
        headerFilter: 'tickCross',
        headerFilterAllowEmpty: true,
        editor: 'tickCross'
      }
    ];

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_desenho, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_desenho}"?`)) {
            excluirRegistro(data.id_desenho);
          }
        },
        headerSort: false
      },
      ...colunasBase.map(col => ({
        ...col,
        editor: col.field === 'id_desenho' ? undefined : (col.editor || 'input')
      }))
    ];
  }, [escolas, atualizarRegistro, excluirRegistro]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: getColunas,
      layout: 'fitData',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_desenho'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
      .naming-convention-modal {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background-color: white;
        padding: 20px;
        border-radius: 8px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        z-index: 1000;
        max-width: 400px;
        text-align: left;
      }
      .naming-convention-modal button {
        margin-top: 15px;
        padding: 8px 15px;
        border-radius: 5px;
        border: none;
        background-color: #007bff;
        color: white;
        cursor: pointer;
      }
      .naming-convention-modal button:hover {
        background-color: #0056b3;
      }
    `;
    document.head.appendChild(style);
  }, [dados, getColunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(getColunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, getColunas, inicializarTabulator]);

  const handleUpload = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.file) {
        throw new Error('Selecione um desenho');
      }
      if (!formData.id_escola) {
        throw new Error('Selecione uma escola');
      }
      if (!formData.id_tipo_representacao) {
        throw new Error('Selecione o tipo de representação');
      }

      const data = new FormData();
      data.append('principal', formData.principal);
      data.append('id_tipo_representacao', formData.id_tipo_representacao);
      data.append('file', formData.file);

      // Recuperar usuário do localStorage
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.id) {
        data.append('usuario', user.id);
      }

      const baseUrl = api.defaults.baseURL;
      const url = `${baseUrl}/uploads/escolas/${formData.id_escola}/desenho`;

      const token = localStorage.getItem('accessToken');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: data
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Erro no upload');
      }

      mostrarMensagem('success', 'Desenho enviado com sucesso!');

      // Resetar form
      setFormData(prev => ({
        ...prev,
        file: null,
        id_tipo_representacao: '',
        principal: true
      }));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Recarregar dados
      carregarDados(false);

    } catch (error) {
      console.error(error);
      mostrarMensagem('error', error.message || 'Erro ao enviar desenho.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFormData(prev => ({ ...prev, file: e.target.files[0] }));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'desenho.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Upload de Desenhos</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Upload de desenhos de escolas</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/desenho.mov" buttonText="Ver Tutorial Desenho" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={handleUpload} className="form-inline">
          <div className="form-group">
            <label htmlFor="file">Desenho:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="file"
              id="file"
              name="file"
              accept=".pdf,.dwg,.dxf"
              onChange={handleFileChange}
              ref={fileInputRef}
              disabled={loading || apiStatus !== 'connected'}
            />
          </div>
          <div className="form-group">
            <label htmlFor="id_escola">Escola:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="id_escola"
              name="id_escola"
              value={escolaSearch}
              onChange={(e) => {
                setEscolaSearch(e.target.value);
                const escolaEncontrada = escolas.find(esc => {
                  const formato = `${esc.nome_escola} (${esc.codigo_inep || esc.id_escola})`;
                  return formato === e.target.value || esc.nome_escola === e.target.value;
                });
                if (escolaEncontrada) {
                  setFormData(prev => ({ ...prev, id_escola: escolaEncontrada.id_escola }));
                } else {
                  setFormData(prev => ({ ...prev, id_escola: '' }));
                }
              }}
              list="escolas-list-desenho"
              placeholder="Digite para buscar escola..."
              disabled={loading || apiStatus !== 'connected'}
              autoComplete="off"
            />
            <datalist id="escolas-list-desenho">
              {escolasFiltradas.map(escola => (
                <option key={escola.id_escola} value={`${escola.nome_escola} (${escola.codigo_inep || escola.id_escola})`} />
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label htmlFor="id_tipo_representacao">Tipo Representação:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <select
              id="id_tipo_representacao"
              name="id_tipo_representacao"
              value={formData.id_tipo_representacao}
              onChange={handleInputChange}
              disabled={loading || apiStatus !== 'connected'}
              required
            >
              <option value="">Selecione...</option>
              {tiposRepresentacao.map(tr => (
                <option key={tr.id_tipo_representacao} value={tr.id_tipo_representacao}>
                  {tr.descricao}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="principal">Principal:</label>
            <input
              type="checkbox"
              id="principal"
              name="principal"
              checked={formData.principal}
              onChange={handleInputChange}
              disabled={loading || apiStatus !== 'connected'}
            />
          </div>

          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Enviando...' : '⬆️ Enviar Desenho'}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
              <button type="button" className="btn btn-info" onClick={() => setShowNamingConventionModal(true)} disabled={loading || apiStatus !== 'connected'}>
                <i className="fas fa-question-circle"></i> Como funcionam os nomes dos pdfs?
              </button>
            </div>

            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span>* indica que os campos são obrigatórios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

      {showNamingConventionModal && (
        <div className="naming-convention-modal">
          <h3>Como nomear os PDFs?</h3>
          <p>Eles seguem o padrão: <strong>sigla da escola - tipo do projeto - item do projeto</strong>.</p>
          <p>Logo, para a prancha 1 de fachada do projeto arquitetônico da escola EEEFM GRACILIANO RAMOS, temos o nome <strong>EEEFMGR-ARQ-FAC1.pdf</strong></p>
          <button onClick={() => setShowNamingConventionModal(false)}>Fechar</button>
        </div>
      )}

    </div>
  );
};

export default DesenhoManager;
